import React, { useMemo, useState } from 'react';
import './Styles.css';
import { formatMessage } from '../../../Localization/formatMessage';
import Messages from '../../../Localization/Messages';
import TextInput from '../../../Elements/ids/InputElements/TextInput';
import CheckBox from '../../../Elements/ids/InputElements/CheckBox';
import { Ui16ArrowSmDown, Ui16Check, Ui24Cross } from '@ids/react-icons';
import { convertServerDateTime } from '../../../Helpers/Converter';
import { InputField, InputSize } from '@ids/react-input-field';
import { IconButton } from '@ids/react-icon-button';
import Select from '../../../Elements/ids/InputElements/Select';
import { isDateInRange } from './Functions';
import ConversionTableMenu from './ConversionTableMenu';

const ConversionTable = ({ agentConversions, setAgentConversions, setNotifications }) => {
  const [filters, setFilters] = useState({
    glCode: '',
    oldCode: '',
    newCode: '',
    channelNameInUserMetadata: '',
    channelNameInWayPoint: '',
    isFutureChangeActive: false,
    dateFrom: '',
    dateTo: '',
  });

  const memoizedAgentConversions = useMemo(
    () =>
      agentConversions
        ?.map(channel =>
          channel.conversions.map(y => ({
            ...y,
            channel: channel,
          }))
        )
        .flat() ?? [],
    [agentConversions]
  );
  const waypointChannels = useMemo(() => agentConversions?.map(channel => channel.channelNameInWayPoint) ?? [], [
    agentConversions,
  ]);
  const userMetaDataChannels = useMemo(
    () => agentConversions?.map(channel => channel.channelNameInUserMetadata) ?? [],
    [agentConversions]
  );

  const filteredConversions = useMemo(() => {
    return memoizedAgentConversions.filter(x => {
      if (filters.glCode && !x.glCode?.toLowerCase().includes(filters.glCode.toLowerCase())) return false;
      if (filters.oldCode && !x.oldCode?.toLowerCase().includes(filters.oldCode.toLowerCase())) return false;
      if (filters.newCode && !x.newCode?.toLowerCase().includes(filters.newCode.toLowerCase())) return false;
      if (filters.isFutureChangeActive && !x.isFutureChangeActive) return false;
      if (!isDateInRange(filters.dateFrom, filters.dateTo, x.isFutureChangeActive ? x.futureChangeStartDate : null))
        return false;
      if (
        filters.channelNameInUserMetadata &&
        !x.channelNameInUserMetadata?.toLowerCase().includes(filters.channelNameInUserMetadata.toLowerCase())
      )
        return false;

      if (
        filters.channelNameInWayPoint &&
        !x.channelNameInWayPoint?.toLowerCase().includes(filters.channelNameInWayPoint.toLowerCase())
      )
        return false;

      return true;
    });
  }, [memoizedAgentConversions, filters]);

  return (
    <div className="agentConversionPage-conversionTable-container">
      <div className="common-table-row agentConversionPage-conversionTable-row headerRow">
        <div className="agentConversionPage-conversionTable-headerColumn">
          <div className="first">{formatMessage(Messages.agentConversionPage_channelNameInUserMetadata)}</div>
          <div className="second">
            <Select
              options={[
                { key: 1, value: '', displayName: '' },
                ...userMetaDataChannels.map(channel => ({ key: channel, value: channel, displayName: channel })),
              ]}
              onChange={event => {
                setFilters({ ...filters, channelNameInUserMetadata: event.target.value });
              }}
            />
          </div>
        </div>
        <div className="agentConversionPage-conversionTable-headerColumn">
          <div className="first">{formatMessage(Messages.agentConversionPage_glCode)}</div>
          <TextInput
            cssClasses="second"
            onChange={event => {
              setFilters({ ...filters, glCode: event.target.value });
            }}
          />
        </div>
        <div className="agentConversionPage-conversionTable-headerColumn">
          <div className="first">{formatMessage(Messages.agentConversionPage_oldCode)}</div>
          <TextInput
            cssClasses="second"
            onChange={event => {
              setFilters({ ...filters, oldCode: event.target.value });
            }}
          />
        </div>
        <div className="agentConversionPage-conversionTable-headerColumn">
          <div className="first">{formatMessage(Messages.agentConversionPage_newCode)}</div>
          <TextInput
            cssClasses="second"
            onChange={event => {
              setFilters({ ...filters, newCode: event.target.value });
            }}
          />
        </div>
        <div className="agentConversionPage-conversionTable-headerColumn">
          <div className="first">{formatMessage(Messages.agentConversionPage_channelNameInWayPoint)}</div>
          <div className="second">
            <Select
              options={[
                { key: 1, value: '', displayName: '' },
                ...waypointChannels.map(channel => ({ key: channel, value: channel, displayName: channel })),
              ]}
              onChange={event => {
                setFilters({ ...filters, channelNameInWayPoint: event.target.value });
              }}
            />
          </div>
        </div>
        <div className="agentConversionPage-conversionTable-headerColumn">
          <div className="first">{formatMessage(Messages.agentConversionPage_convertedAgentsInRenewalStep)}</div>
          <CheckBox
            cssClasses="second"
            checked={filters.isFutureChangeActive}
            onChange={() => setFilters({ ...filters, isFutureChangeActive: !filters.isFutureChangeActive })}
          />
        </div>
        <div className="agentConversionPage-conversionTable-headerColumn">
          <div className="first">{formatMessage(Messages.agentConversionPage_convertedDate)}</div>
          <div>
            <InputField
              type="date"
              inputSize={InputSize.SMALL}
              value={filters.dateFrom}
              onChange={event => {
                setFilters({ ...filters, dateFrom: event.target.value });
              }}
            />
            <div className="agentConversionPage-conversionTable-dateArrow">
              <Ui16ArrowSmDown />
            </div>

            <InputField
              type="date"
              inputSize={InputSize.SMALL}
              value={filters.dateTo}
              onChange={event => {
                setFilters({ ...filters, dateTo: event.target.value });
              }}
            />
          </div>
        </div>
        <div className="agentConversionPage-conversionTable-dateInputClearIcon">
          <IconButton type={'plain'} variant={'quiet'}>
            <Ui24Cross
              onClick={() => {
                setFilters({ ...filters, dateFrom: '', dateTo: '' });
              }}
            />
          </IconButton>
        </div>
      </div>
      {filteredConversions?.map((x, index) => (
        <div
          className={`common-table-row agentConversionPage-conversionTable-row ${
            index % 2 === 1 ? 'common-table-evenRow' : ''
          } ${x.isFutureChangeActive ? 'futureChangeActive' : ''}`}
          key={index}>
          <div>{x.channelNameInUserMetadata}</div>
          <div>{x.glCode}</div>
          <div>{x.oldCode}</div>
          <div>{x.newCode}</div>
          <div>{x.channelNameInWayPoint}</div>
          <div>{x.isFutureChangeActive && <Ui16Check />}</div>
          <div>{x.isFutureChangeActive && x.newCode && convertServerDateTime(x.futureChangeStartDate)}</div>
          <div className="agentConversionPage-conversionTable-menuContainer">
            <ConversionTableMenu
              conversion={x}
              setAgentConversions={setAgentConversions}
              agentConversions={agentConversions}
              setNotifications={setNotifications}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default ConversionTable;
